/**
 * WorkOS Authentication Service for React Router App
 * Following the complete API reference patterns exactly
 */

import {
    applyDeploymentRoutingTierFromUserProfile,
    mergeDeploymentRoutingHeaders,
} from "@/utils/deploymentRouting";
import { clearSessionScopedLocalStorage } from "@/utils/clearSessionLocalStorage";
import { ensureLogoutTrace, logoutTrace } from "../../utils/logoutTrace";

interface UserProfile {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    name?: string;
    display_name?: string;
    company_id: string;
    company_name?: string;
    role?: string;
    email_verified: boolean;
    profile_picture_url?: string;
    last_login?: string;
    permissions?: string[];
    modules?: string[];
    sso_enabled?: boolean;
    /** Pattern D-b: optional ingress routing hint from BE */
    deployment_routing_tier?: "free" | "standard" | null;
    welcome_screen?: {
        enabled: boolean;
        body: string;
        steps: string[];
    } | null;
}

interface WorkOSCallbackResponse {
    success: boolean;
    message: string;
    user?: UserProfile;
}

interface WorkOSStatusResponse {
    authenticated: boolean;
    email?: string;
    login_time?: string;
}

interface WorkOSMeResponse {
    success: boolean;
    user?: UserProfile;
}

interface WorkOSLogoutResponse {
    success: boolean;
    message: string;
}

interface SSOCheckResponse {
    sso_enabled: boolean;
    sso_provider?: string;
    organization_id?: string;
    organization_name?: string;
    domain?: string;
    auth_method?: string;
}

interface EmailPasswordLoginResponse {
    success: boolean;
    message: string;
    user?: UserProfile;
    auth_token?: string;  // Bearer token for non-SSO users
    mfa_required?: boolean;
    mfa_enrollment?: boolean;
    pending_authentication_token?: string;
    authentication_challenge_id?: string;
    totp_uri?: string;
}

export class MfaRequiredError extends Error {
    readonly mfaRequired = true;

    constructor(
        public pendingAuthenticationToken: string,
        public authenticationChallengeId: string,
        public enrollment: boolean,
        public totpUri?: string,
    ) {
        super('Enter the 6-digit code from your authenticator app');
        this.name = 'MfaRequiredError';
    }
}

interface PasswordResetResponse {
    success: boolean;
    message: string;
    auth_method?: string;
}

/**
 * Registration request data.
 */
export interface RegistrationRequest {
    email: string;
    first_name: string;
    last_name: string;
    company: string;
    password: string;
}

/**
 * Registration response user data.
 */
export interface RegistrationUser {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    company_name: string;
    status: 'pending' | 'approved' | 'rejected';
}

/**
 * Registration API response.
 */
export interface RegistrationResponse {
    success: boolean;
    message: string;
    user: RegistrationUser;
    requires_approval: boolean;
}

/**
 * Email availability check response.
 */
export interface CheckEmailResponse {
    available: boolean;
    reason: null;
    sso_required: boolean;
    sso_provider: 'google' | 'microsoft' | null;
    organization_name: string | null;
}

/**
 * Registration status response.
 */
export interface RegistrationStatus {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    company_name: string;
    status: 'pending' | 'approved' | 'rejected';
    rejected_reason: string | null;
    created_at: string;
}

/**
 * Registration error response detail.
 */
interface RegistrationErrorDetail {
    success: boolean;
    error: string;
    message: string;
    field?: string;
    sso_provider?: string;
}

export class WorkOSAuthService {
    private apiBaseUrl: string;

    constructor() {
        // Use existing environment variable pattern from your app
        let baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';

        // Ensure localhost always uses HTTP (not HTTPS) to avoid protocol mismatch
        if (baseUrl.includes('localhost') && baseUrl.startsWith('https://')) {
            baseUrl = baseUrl.replace('https://', 'http://');
        }

        this.apiBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    }

    /**
     * Check if email/domain has SSO enabled
     * Used for email-first authentication flow
     */
    async checkSSO(email: string): Promise<SSOCheckResponse> {
        const response = await fetch(`${this.apiBaseUrl}/auth/check-sso`, {
            method: 'POST',
            credentials: 'include',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            throw new Error(`Failed to check SSO status: ${response.status}`);
        }

        const data: SSOCheckResponse = await response.json();
        return data;
    }

    /**
     * Initiate Google OAuth via WorkOS
     * Redirects to backend which handles WorkOS OAuth flow
     * Optionally pre-fill email if provided
     */
    async login(email?: string): Promise<void> {
        // Direct redirect to backend login endpoint - no state management needed
        // Backend handles WorkOS OAuth flow entirely
        let loginUrl = `${this.apiBaseUrl}/auth/workos/login`;
        
        // Add email parameter if provided (for pre-filling in OAuth)
        if (email) {
            loginUrl += `?email=${encodeURIComponent(email)}`;
        }
        
        window.location.href = loginUrl;
    }

    /**
     * Best-effort: invalidate the WorkOS session cookie before password login.
     * Without this, a stale HttpOnly cookie sent with `credentials: 'include'`
     * can cause the server to resolve the previous user instead of the credentials
     * in the request body.
     */
    private async clearWorkosSessionBeforePasswordLogin(): Promise<void> {
        try {
            await fetch(`${this.apiBaseUrl}/auth/workos/logout`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
            });
        } catch {
            // Non-fatal: login still proceeds
        }
    }

    /**
     * Traditional email/password login
     * Used when SSO is not enabled for the user
     * Returns user data and stores Bearer token for API requests
     */
    async loginWithPassword(email: string, password: string): Promise<UserProfile> {
        const normalizedRequested = email.trim().toLowerCase();

        await this.clearWorkosSessionBeforePasswordLogin();

        const response = await fetch(`${this.apiBaseUrl}/auth/login`, {
            method: 'POST',
            credentials: 'include',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }

            throw new Error(errorData.detail || errorData.message || 'Invalid email or password');
        }

        const data: EmailPasswordLoginResponse = await response.json();

        if (data.mfa_required) {
            if (!data.pending_authentication_token || !data.authentication_challenge_id) {
                throw new Error('MFA challenge is incomplete. Please try signing in again.');
            }
            throw new MfaRequiredError(
                data.pending_authentication_token,
                data.authentication_challenge_id,
                data.mfa_enrollment === true,
                data.totp_uri,
            );
        }

        if (!data.success || !data.user) {
            throw new Error(data.message || 'No user data received from authentication');
        }

        const returnedEmail = (data.user.email ?? '').trim().toLowerCase();
        if (
            normalizedRequested.length > 0 &&
            returnedEmail.length > 0 &&
            returnedEmail !== normalizedRequested
        ) {
            throw new Error(
                'The signed-in account does not match the email you entered. Try signing out fully, then sign in again, or contact support if this keeps happening.'
            );
        }

        // Store Bearer token for non-SSO users
        if (data.auth_token) {
            const authData = {
                auth_token: data.auth_token,
                user: data.user,
                last_updated: Date.now()
            };
            localStorage.setItem('auth_data', JSON.stringify(authData));
        }

        applyDeploymentRoutingTierFromUserProfile(data.user);
        return data.user;
    }

    async verifyMfa(params: {
        email: string;
        code: string;
        pendingAuthenticationToken: string;
        authenticationChallengeId: string;
    }): Promise<UserProfile> {
        const normalizedRequested = params.email.trim().toLowerCase();
        const response = await fetch(`${this.apiBaseUrl}/auth/mfa/verify`, {
            method: 'POST',
            credentials: 'include',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                email: params.email,
                code: params.code,
                pending_authentication_token: params.pendingAuthenticationToken,
                authentication_challenge_id: params.authenticationChallengeId,
            }),
        });

        if (!response.ok) {
            let errorData: { detail?: string; message?: string };
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }
            throw new Error(errorData.detail || errorData.message || 'Invalid authentication code');
        }

        const data: EmailPasswordLoginResponse = await response.json();
        if (!data.success || !data.user) {
            throw new Error(data.message || 'No user data received from authentication');
        }

        const returnedEmail = (data.user.email ?? '').trim().toLowerCase();
        if (
            normalizedRequested.length > 0 &&
            returnedEmail.length > 0 &&
            returnedEmail !== normalizedRequested
        ) {
            throw new Error(
                'The signed-in account does not match the email you entered. Try signing out fully, then sign in again, or contact support if this keeps happening.'
            );
        }

        if (data.auth_token) {
            const authData = {
                auth_token: data.auth_token,
                user: data.user,
                last_updated: Date.now()
            };
            localStorage.setItem('auth_data', JSON.stringify(authData));
        }

        applyDeploymentRoutingTierFromUserProfile(data.user);
        return data.user;
    }

    async getMfaStatus(): Promise<{ available: boolean; enrolled: boolean; reason?: string }> {
        const response = await this.authenticatedFetch('/auth/mfa/status', { method: 'GET' });
        if (!response.ok) {
            throw new Error('Could not load MFA status');
        }
        return response.json();
    }

    async startMfaEnrollment(): Promise<{ authentication_challenge_id: string; totp_uri?: string }> {
        const response = await this.authenticatedFetch('/auth/mfa/enroll', { method: 'POST' });
        if (!response.ok) {
            let errorData: { detail?: string; message?: string };
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }
            throw new Error(errorData.detail || errorData.message || 'Could not start MFA enrollment');
        }
        return response.json();
    }

    async confirmMfaEnrollment(code: string, authenticationChallengeId: string): Promise<void> {
        const response = await this.authenticatedFetch('/auth/mfa/enroll/confirm', {
            method: 'POST',
            body: JSON.stringify({
                code,
                authentication_challenge_id: authenticationChallengeId,
            }),
        });
        if (!response.ok) {
            let errorData: { detail?: string; message?: string };
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }
            throw new Error(errorData.detail || errorData.message || 'Invalid authentication code');
        }
    }

    /**
     * Handle OAuth callback from WorkOS
     * Called from the callback route component
     */
    async handleCallback(code: string, state: string): Promise<UserProfile> {
        const callbackUrl = `${this.apiBaseUrl}/auth/workos/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;

        const response = await fetch(callbackUrl, {
            method: 'GET',
            credentials: 'include', // Essential for session cookies
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            redirect: 'manual', // Don't follow redirects automatically
        });

        // Check if response is a redirect (backend might redirect instead of returning JSON)
        if (response.type === 'opaqueredirect' || response.status === 302 || response.status === 301) {
            // Backend set the session cookie and is redirecting
            // We need to get the user info separately
            try {
                const user = await this.getCurrentUser();
                return user;
            } catch (e) {
                console.error('Failed to get user after redirect:', e);
                throw new Error('Authentication callback succeeded but session not established. Please try logging in again.');
            }
        }

        if (!response.ok) {
            let errorData;
            try {
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    errorData = await response.json();
                } else {
                    // Response is HTML or other non-JSON format
                    const text = await response.text();
                    console.error('Non-JSON response:', text.substring(0, 200));
                    errorData = { detail: `Authentication failed: ${response.status}` };
                }
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }

            throw new Error(errorData.detail || `Authentication failed: ${response.status}`);
        }

        // Check if response is JSON before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            // Backend might have set cookie and returned HTML
            try {
                const user = await this.getCurrentUser();
                return user;
            } catch (e) {
                console.error('Failed to get user after non-JSON response:', e);
                throw new Error('Authentication callback succeeded but session not established. Please try logging in again.');
            }
        }

        const data: WorkOSCallbackResponse = await response.json();

        if (!data.success || !data.user) {
            throw new Error(data.message || 'No user data received from authentication');
        }

        applyDeploymentRoutingTierFromUserProfile(data.user);
        return data.user;
    }

    /**
     * Get current authenticated user information
     */
    async getCurrentUser(route?: string): Promise<UserProfile> {
        // Add cache-busting to prevent stale user data
        // Using both timestamp query param AND fetch cache: 'no-store' for maximum reliability
        const params = new URLSearchParams({ _t: String(Date.now()) });
        if (route) {
            params.set('route', route);
        }
        const url = `${this.apiBaseUrl}/auth/workos/me?${params.toString()}`;
        
        const response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            cache: 'no-store', // Bypass browser cache entirely
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
            }),
        });

        if (!response.ok) {
            if (response.status === 401) {
                throw new Error('Not authenticated');
            }
            throw new Error(`Failed to get user: ${response.status}`);
        }

        const data: WorkOSMeResponse = await response.json();

        if (!data.success || !data.user) {
            throw new Error('Invalid response from server');
        }

        applyDeploymentRoutingTierFromUserProfile(data.user);
        return data.user;
    }

    /**
     * Check current authentication status
     */
    async checkAuthStatus(): Promise<{ authenticated: boolean; email?: string; login_time?: string }> {
        try {
            // Add cache-busting to prevent browser from caching auth status
            // Using both timestamp query param AND fetch cache: 'no-store' for maximum reliability
            const cacheBuster = `_t=${Date.now()}`;
            const url = `${this.apiBaseUrl}/auth/workos/status?${cacheBuster}`;
            
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                cache: 'no-store', // Bypass browser cache entirely
                headers: mergeDeploymentRoutingHeaders({
                    'Accept': 'application/json',
                }),
            });

            if (!response.ok) {
                return { authenticated: false };
            }

            const data: WorkOSStatusResponse = await response.json();
            return data;
        } catch (error) {
            console.error('[checkAuthStatus] Error checking auth status:', error);
            return { authenticated: false };
        }
    }

    /**
     * Logout user and clear session
     * Makes a request to the backend to invalidate the server-side session
     * and clear the HttpOnly session cookie
     */
    async logout(): Promise<void> {
        ensureLogoutTrace();
        logoutTrace("workos.logout.start", {
            apiBase: this.apiBaseUrl,
        });

        // Get Bearer token BEFORE any logout calls (it will be cleared after)
        const authDataStr = localStorage.getItem('auth_data');
        let bearerToken: string | null = null;

        logoutTrace("workos.logout.auth_data", { present: !!authDataStr });

        if (authDataStr) {
            try {
                const authData = JSON.parse(authDataStr);
                if (authData.auth_token) {
                    bearerToken = authData.auth_token;
                    logoutTrace("workos.logout.bearer", {
                        tokenLength: authData.auth_token.length,
                    });
                }
            } catch {
                // ignore invalid auth_data
            }
        }

        const workosPostStarted = typeof performance !== "undefined" ? performance.now() : 0;
        logoutTrace("workos.logout.post.request", {
            url: `${this.apiBaseUrl}/auth/workos/logout`,
        });
        try {
            const response = await fetch(`${this.apiBaseUrl}/auth/workos/logout`, {
                method: 'POST',
                credentials: 'include', // Essential - sends the session cookie to be cleared
                headers: mergeDeploymentRoutingHeaders({
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                }),
            });

            let responseBody: unknown = {};
            if (typeof response.json === "function") {
                try {
                    responseBody = await response.json();
                } catch {
                    responseBody = {};
                }
            }
            const fetchMs =
                typeof performance !== "undefined"
                    ? (performance.now() - workosPostStarted).toFixed(1)
                    : "—";
            logoutTrace("workos.logout.post.response", {
                status: response.status,
                statusText: response.statusText,
                ok: response.ok,
                fetchMs,
                body: responseBody,
                visibleCookiesLength: typeof document !== "undefined" ? document.cookie.length : 0,
            });
        } catch (error) {
            console.error('[AuthLogout] workos.logout.post failed:', error);
            logoutTrace("workos.logout.post.error", {
                message: error instanceof Error ? error.message : String(error),
            });
        }

        if (bearerToken) {
            const bearerStarted = typeof performance !== "undefined" ? performance.now() : 0;
            logoutTrace("workos.logout.bearer_post.request");
            try {
                const response = await fetch(`${this.apiBaseUrl}/auth/logout`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: mergeDeploymentRoutingHeaders({
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${bearerToken}`,
                    }),
                });

                const bearerMs =
                    typeof performance !== "undefined"
                        ? (performance.now() - bearerStarted).toFixed(1)
                        : "—";
                logoutTrace("workos.logout.bearer_post.response", {
                    status: response.status,
                    fetchMs: bearerMs,
                });
            } catch (error) {
                console.error('[AuthLogout] bearer logout request failed:', error);
                logoutTrace("workos.logout.bearer_post.error", {
                    message: error instanceof Error ? error.message : String(error),
                });
            }
        } else {
            logoutTrace("workos.logout.bearer_post.skip", {
                reason: "no_bearer_sso_user",
            });
        }

        logoutTrace("workos.logout.localStorage.before", {
            auth_data: !!localStorage.getItem('auth_data'),
            workos_user: !!localStorage.getItem('workos_user'),
        });

        try {
            clearSessionScopedLocalStorage();

            logoutTrace("workos.logout.localStorage.after", {
                auth_data: !!localStorage.getItem('auth_data'),
                workos_user: !!localStorage.getItem('workos_user'),
            });
        } catch (e) {
            console.error('[AuthLogout] localStorage clear failed:', e);
            logoutTrace("workos.logout.localStorage.error", {
                message: e instanceof Error ? e.message : String(e),
            });
        }

        logoutTrace("workos.logout.complete");
    }

    /**
     * Request password reset for a user
     * Sends a password reset email via WorkOS
     */
    async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
        const response = await fetch(`${this.apiBaseUrl}/auth/request-password-reset`, {
            method: 'POST',
            credentials: 'include',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            let errorData;
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }

            throw new Error(errorData.detail || errorData.message || 'Failed to send password reset email');
        }

        const data: PasswordResetResponse = await response.json();
        return data;
    }

    /**
     * Change password for the signed-in user (requires current password).
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        const response = await fetch(`${this.apiBaseUrl}/auth/change-password`, {
            method: 'POST',
            credentials: 'include',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({
                current_password: currentPassword,
                new_password: newPassword,
            }),
        });

        if (!response.ok) {
            let errorData: { detail?: string | { message?: string }; message?: string };
            try {
                errorData = await response.json();
            } catch {
                errorData = { detail: `HTTP ${response.status}` };
            }

            const detail = errorData.detail;
            const message =
                typeof detail === 'string'
                    ? detail
                    : detail?.message || errorData.message || 'Failed to change password';
            throw new Error(message);
        }
    }

    /**
     * Utility method for authenticated requests to other endpoints
     */
    async authenticatedFetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
        const url = endpoint.startsWith('http') ? endpoint : `${this.apiBaseUrl}${endpoint}`;

        const baseHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...(options.headers as Record<string, string> | undefined),
        };
        return fetch(url, {
            ...options,
            credentials: 'include',
            headers: mergeDeploymentRoutingHeaders(baseHeaders),
        });
    }

    // =========================================================================
    // REGISTRATION METHODS
    // =========================================================================

    /**
     * Check if an email is available for registration.
     * Also returns SSO requirements if applicable.
     *
     * @param email - The email address to check
     * @returns Email availability status and SSO info
     */
    async checkEmailAvailability(email: string): Promise<CheckEmailResponse> {
        const response = await fetch(`${this.apiBaseUrl}/auth/check-email`, {
            method: 'POST',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            throw new Error(`Failed to check email availability: ${response.status}`);
        }

        return response.json();
    }

    /**
     * Register a new user with email and password.
     * New registrations require admin approval before the user can login.
     *
     * @param data - Registration form data
     * @returns Registration response with user details and approval status
     * @throws Error with specific message for validation errors, duplicate emails, etc.
     */
    async registerUser(data: RegistrationRequest): Promise<RegistrationResponse> {
        const response = await fetch(`${this.apiBaseUrl}/auth/register`, {
            method: 'POST',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            }),
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            let errorDetail: RegistrationErrorDetail | undefined;
            try {
                const errorData = await response.json();
                errorDetail = errorData.detail;
            } catch {
                // Response might not be JSON
            }

            if (errorDetail?.message) {
                const error = new Error(errorDetail.message) as Error & { 
                    code?: string; 
                    field?: string;
                    ssoProvider?: string;
                };
                error.code = errorDetail.error;
                error.field = errorDetail.field;
                error.ssoProvider = errorDetail.sso_provider;
                throw error;
            }

            throw new Error('Registration failed. Please try again.');
        }

        return response.json();
    }

    /**
     * Initiate OAuth registration via WorkOS (Google or Microsoft).
     * Redirects to the OAuth provider for new user signup.
     *
     * @param provider - OAuth provider ('google' or 'microsoft')
     */
    registerWithOAuth(provider: 'google' | 'microsoft'): void {
        window.location.href = `${this.apiBaseUrl}/auth/workos/register?provider=${provider}`;
    }

    /**
     * Check the status of a submitted registration.
     *
     * @param registrationId - The registration ID returned from registerUser
     * @returns Current registration status
     * @throws Error if registration not found
     */
    async checkRegistrationStatus(registrationId: string): Promise<RegistrationStatus> {
        const response = await fetch(`${this.apiBaseUrl}/auth/registration-status/${registrationId}`, {
            method: 'GET',
            headers: mergeDeploymentRoutingHeaders({
                'Accept': 'application/json',
            }),
        });

        if (!response.ok) {
            if (response.status === 404) {
                throw new Error('Registration not found');
            }
            throw new Error(`Failed to check registration status: ${response.status}`);
        }

        return response.json();
    }
}

// Export singleton instance
export const workosAuth = new WorkOSAuthService();


